/**
 * Custom hook for file deletion with TanStack Query
 * Follows same patterns as useFileUpload
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import {
  deleteFiles,
  DeleteFilesRequest,
  UploadResponse
} from '../api/uploads';
import { UploadApiError } from '../api/base/errors';
import { conversationQueryKeys } from '../api/conversations';
import { showNotification } from '../components/Notification';
import { useChatStore } from '../stores/chatStore';


export interface UseFileDeleteOptions {
  onDeleteStart?: (filenames: string[]) => void;
  onDeleteSuccess?: (response: UploadResponse) => void;
  onDeleteError?: (error: UploadApiError) => void;
  onComplete?: () => void;
  skipInvalidate?: boolean;
}

export interface UseFileDeleteReturn {
  deleteFiles: (request: DeleteFilesRequest) => Promise<UploadResponse>;
  isDeleting: boolean;
  deleteError: UploadApiError | null;
  reset: () => void;
}

export function useFileDelete(
  conversationId: string,
  options: UseFileDeleteOptions = {}
): UseFileDeleteReturn {
  const queryClient = useQueryClient();
  const {
    onDeleteStart,
    onDeleteSuccess,
    onDeleteError,
    onComplete,
    skipInvalidate = false
  } = options;

  const deleteMutation = useMutation({
    mutationKey: ['delete-files', conversationId],
    mutationFn: deleteFiles,
    onMutate: async (variables: DeleteFilesRequest) => {
      onDeleteStart?.(variables.filenames);

      // Ignore any conversation response that started before this mutation. It
      // can contain the previous operation's file/status snapshot and otherwise
      // race the delete acknowledgement below.
      await queryClient.cancelQueries({
        queryKey: conversationQueryKeys.detail(conversationId),
      });

      return {
        deleteStartTime: Date.now(),
        fileCount: variables.filenames.length,
        previousConversationData: undefined,
      };
    },
    onSuccess: (response: UploadResponse, variables) => {
      // The backend writes processing_status before acknowledging the request.
      // Start polling only after that acknowledgement so an old cached
      // "complete" value cannot terminate this delete cycle prematurely.
      useChatStore.getState().setFileProcessingStatus(
        response.conversation_id || variables.conversationId,
        'processing',
      );

      onDeleteSuccess?.(response);

      // Do NOT clear fileProcessingStatus here — the server is still processing
      // the deletion in the background (search-index cleanup, etc.).
      // The WebSocket 'upload_complete' event or useUploadStatusPoll will
      // clear it when the server finishes, matching the upload flow.

      // Invalidate conversation queries to refresh uploaded files list unless explicitly skipped
      if (!skipInvalidate) {
        queryClient.invalidateQueries({
          queryKey: conversationQueryKeys.detail(conversationId),
        });
      }
      showNotification("Your files have been updated");

      onComplete?.();
    },
    onError: (error: UploadApiError, variables, context) => {
      console.error('File deletion failed:', {
        error: error.message,
        status: error.status,
        filenames: variables.filenames,
        context,
      });

      // Clear processing banner on error — the operation failed so there is
      // nothing to wait for.
      useChatStore.getState().clearFileProcessingStatus(conversationId);
      variables.filenames.forEach((filename) => {
        useChatStore.getState().markFileUploaded(conversationId, filename);
      });

      onDeleteError?.(error);
      onComplete?.();
    },
  });

  const deleteFilesWrapper = useCallback(async (request: DeleteFilesRequest): Promise<UploadResponse> => {
    if (!request.filenames.length) {
      throw new UploadApiError('No files selected for deletion', 400);
    }

    if (!request.conversationId) {
      throw new UploadApiError('Conversation ID is required', 400);
    }

    return deleteMutation.mutateAsync(request);
  }, [deleteMutation]);

  return {
    deleteFiles: deleteFilesWrapper,
    isDeleting: deleteMutation.isPending,
    deleteError: deleteMutation.error,
    reset: deleteMutation.reset,
  };
}
